import { useMutation, useQueryClient } from "@tanstack/react-query";
import qcareApi from "./qcareApi";
import { Defects, DetailName } from "../interfaces/interfaces";
import { ImageType } from "../interfaces/intakes.reports";

interface BasicData {
    reportId: string
    palletId: string
}

interface ReorderImagesData extends BasicData {
    newImages: ImageType[]
}
interface RotatedImage {
    reportId: string
    newImage: File
    oldImage: ImageType
}


const reorderImages = async ({ reportId, palletId, newImages }: ReorderImagesData) => {
    const { data } = await qcareApi.put("/report/reorder-images", { reportId, palletId, newImages })
    return data
};

const sendRotateImage = async ({ reportId,  newImage, oldImage }: RotatedImage) => {
    const formData = new FormData();

    formData.append('replace-image', newImage)
    formData.append('oldImage', JSON.stringify(oldImage))
    formData.append('reportId', reportId)

    const { data } = await qcareApi.put('/report/replace-image', formData)
    return data
};

// The rotated image is re-uploaded to the same DigitalOcean Spaces key, so
// imgURL/imgURL_low never change value. React (and the browser's image cache)
// won't reload an <img> whose src attribute is unchanged, so we have to force
// a cache-bust on the matching image entry ourselves after a refetch.
const withCacheBust = (url: string, version: number) => `${url.split('?')[0]}?v=${version}`;

const bustImageCache = (data: any, oldImage: ImageType, version: number): any => {
    if (Array.isArray(data)) {
        return data.map((item) => bustImageCache(item, oldImage, version))
    }

    if (data && typeof data === 'object') {
        if (data.key === oldImage.key && data.key_low === oldImage.key_low) {
            return {
                ...data,
                imgURL: withCacheBust(data.imgURL, version),
                imgURL_low: withCacheBust(data.imgURL_low, version),
            }
        }

        const next: any = {}
        for (const [key, value] of Object.entries(data)) {
            next[key] = bustImageCache(value, oldImage, version)
        }
        return next
    }

    return data
};

// ------------------------- HOOKS -------------------------

export const useReorderImages = () => {
    const queryClient = useQueryClient()
    return useMutation(reorderImages, {
        onSuccess: async (data) => {
            await queryClient.invalidateQueries(['report', data.reportId])
        }
    })
}

export const useRotateImage = () => {
    const queryClient = useQueryClient()
    return useMutation(sendRotateImage, {
        onSuccess: async (data, variables) => {
            // A finished report is cached under 'report' (useReport, Report.tsx);
            // a report still being edited is cached under 'reportTemp' (useTempReport,
            // CreatePageSteps.tsx). Either can be the one currently open, so refresh both.
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['report', data.reportId], refetchType: 'all' }),
                queryClient.invalidateQueries({ queryKey: ['reportTemp', data.reportId], refetchType: 'all' }),
            ])

            const version = Date.now()
            for (const queryKey of [['report', data.reportId], ['reportTemp', data.reportId]]) {
                queryClient.setQueryData(queryKey, (old: any) => {
                    if (!old) return old
                    return bustImageCache(old, variables.oldImage, version)
                })
            }
        }
    })
}

// export const useEditComments = () => {
//     const queryClient = useQueryClient()
//     return useMutation(editComments, {
//         onSuccess: async (data) => { await queryClient.invalidateQueries(['report', data.reportId],) }
//     })
// }

// export const useEditPalletComments = () => {
//     const queryClient = useQueryClient()
//     return useMutation(editPalletComments, {
//         onSuccess: async (data) => { await queryClient.invalidateQueries(['report', data.reportId],) }
//     })
// }

// export const useDeleteItem = () => {
//     const queryClient = useQueryClient()
//     return useMutation(deleteItem, {
//         onSuccess: async (item) => { await queryClient.invalidateQueries([item.type]) }
//     })
// }

// export const useAddNewItem = () => {
//     const queryClient = useQueryClient()
//     return useMutation(addNewItem, {
//         onSuccess: async (data) => { await queryClient.invalidateQueries(['report', data.reportId],) }
//     })
// }
